import axios from 'axios';

/**
 * Sales rank history, read for the whole screen rather than a row at a time.
 *
 * Every row of the vendor inventory report draws a sparkline, and each cell used
 * to fetch its own history. A thirty row page therefore made thirty requests,
 * and the api throttle allows an authenticated user two hundred a minute, so
 * three views of that screen spent the budget. What followed was not a slow
 * screen but a dead application: /api/user is throttled with everything else, so
 * every screen rendered blank for the rest of the minute.
 *
 * Callers still ask for one product. The ids asked for in the same tick are
 * collected here and read in one request, so the component keeps its simple
 * shape and the screen stays inside the throttle.
 */

/** Matches MAX_PRODUCTS on AmazonProductProductDataHistoryController. */
const MAX_PER_REQUEST = 100;

/** Product id => the waiters for it. */
let pending = new Map();

let flushScheduled = false;

function flush() {
    const batch = pending;

    pending = new Map();
    flushScheduled = false;

    const ids = Array.from(batch.keys());

    for (let start = 0; start < ids.length; start += MAX_PER_REQUEST) {
        const chunk = ids.slice(start, start + MAX_PER_REQUEST);

        axios.get('/api/amazonproducts/productDataHistory', { params: { ids: chunk.join(',') } })
            .then(({ data }) => {
                const histories = (data && data.data) || {};

                chunk.forEach((id) => {
                    const entries = histories[id] || [];

                    // A copy each, because the caller reverses the array it is
                    // given and two rows can name the same product.
                    batch.get(id).forEach(({ resolve }) => resolve({ data: entries.slice() }));
                });
            })
            .catch((error) => {
                chunk.forEach((id) => batch.get(id).forEach(({ reject }) => reject(error)));
            });
    }
}

/**
 * Three months of history for one product, in the shape the single product
 * endpoint used to return.
 *
 * @param {number|string} productId
 * @returns {Promise<{data: Array}>}
 */
export function fetchProductDataHistory(productId) {
    return new Promise((resolve, reject) => {
        const id = Number(productId);

        if (!pending.has(id)) {
            pending.set(id, []);
        }

        pending.get(id).push({ resolve, reject });

        if (!flushScheduled) {
            flushScheduled = true;

            // A timeout rather than a microtask: the cells are created over the
            // course of one render, and this collects the whole of it.
            setTimeout(flush, 0);
        }
    });
}
